import { useState, useMemo, useEffect, useCallback } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useAppContext } from '@/hooks/useAppContext';
import { useToast } from '@/hooks/useToast';
import { useNWC } from '@/hooks/useNWCContext';
import type { NWCConnection } from '@/hooks/useNWC';
import { nip57 } from 'nostr-tools';
import type { Event } from 'nostr-tools';
import type { WebLNProvider } from '@webbtc/webln-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

export function useZaps(
  target: Event | Event[] | null,
  webln: WebLNProvider | null,
  _nwcConnection: NWCConnection | null,
  recipientPubkey?: string,
  onZapSuccess?: () => void
) {
  const { nostr } = useNostr();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const queryClient = useQueryClient();

  // Handle the case where an empty array is passed (from ZapButton when external data is provided)
  const actualTarget = Array.isArray(target) ? (target.length > 0 ? target[0] : null) : target;

  // For profile zaps, use the provided recipientPubkey. For event zaps, use the event's pubkey
  const targetPubkey = recipientPubkey || actualTarget?.pubkey;

  const author = useAuthor(targetPubkey);
  const { sendPayment, getActiveConnection } = useNWC();
  const [isZapping, setIsZapping] = useState(false);
  const [invoice, setInvoice] = useState<string | null>(null);
  const [pendingInvoice, setPendingInvoice] = useState<string | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Multi-invoice state for manual payment of splits
  const [pendingInvoices, setPendingInvoices] = useState<Array<{ pubkey: string; amount: number; invoice: string }>>([]);
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(0);

  // Cleanup state when component unmounts
  useEffect(() => {
    return () => {
      setIsZapping(false);
      setInvoice(null);
      setPendingInvoice(null);
      setPaymentConfirmed(false);
      setPendingInvoices([]);
      setCurrentInvoiceIndex(0);
    };
  }, []);

  const { data: zapEvents, ...query } = useQuery<NostrEvent[], Error>({
    queryKey: ['zaps', actualTarget?.id || targetPubkey],
    staleTime: 30000, // 30 seconds
    refetchInterval: (query) => {
      // Only refetch if the query is currently being observed (component is mounted)
      // Poll more frequently (every 2 seconds) when we have a pending invoice
      if (pendingInvoice && query.getObserversCount() > 0) {
        return 2000;
      }
      return query.getObserversCount() > 0 ? 60000 : false;
    },
    queryFn: async (c) => {
      if (!actualTarget && !targetPubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // If no event, query for profile zaps (zaps with only p tag, no e or a tag)
      if (!actualTarget && targetPubkey) {
        const events = await nostr.query([{
          kinds: [9735],
          '#p': [targetPubkey],
        }], { signal });
        return events;
      }

      // Query for zap receipts for this specific event
      if (actualTarget!.kind >= 30000 && actualTarget!.kind < 40000) {
        // Addressable event
        const identifier = actualTarget!.tags.find((t) => t[0] === 'd')?.[1] || '';
        const events = await nostr.query([{
          kinds: [9735],
          '#a': [`${actualTarget!.kind}:${actualTarget!.pubkey}:${identifier}`],
        }], { signal });
        return events;
      } else {
        // Regular event
        const events = await nostr.query([{
          kinds: [9735],
          '#e': [actualTarget!.id],
        }], { signal });
        return events;
      }
    },
    enabled: !!(actualTarget?.id || targetPubkey),
  });

  // Process zap events into simple counts and totals
  const { zapCount, totalSats, zaps } = useMemo(() => {
    if (!zapEvents || !Array.isArray(zapEvents)) {
      return { zapCount: 0, totalSats: 0, zaps: [] };
    }

    let count = 0;
    let sats = 0;

    zapEvents.forEach(zap => {
      count++;

      // Try multiple methods to extract the amount:

      // Method 1: amount tag (from zap request, sometimes copied to receipt)
      const amountTag = zap.tags.find(([name]) => name === 'amount')?.[1];
      if (amountTag) {
        const millisats = parseInt(amountTag);
        sats += Math.floor(millisats / 1000);
        return;
      }

      // Method 2: Extract from bolt11 invoice
      const bolt11Tag = zap.tags.find(([name]) => name === 'bolt11')?.[1];
      if (bolt11Tag) {
        try {
          const invoiceSats = nip57.getSatoshisAmountFromBolt11(bolt11Tag);
          sats += invoiceSats;
          return;
        } catch (error) {
          console.warn('Failed to parse bolt11 amount:', error);
        }
      }

      // Method 3: Parse from description (zap request JSON)
      const descriptionTag = zap.tags.find(([name]) => name === 'description')?.[1];
      if (descriptionTag) {
        try {
          const zapRequest = JSON.parse(descriptionTag);
          const requestAmountTag = zapRequest.tags?.find(([name]: string[]) => name === 'amount')?.[1];
          if (requestAmountTag) {
            const millisats = parseInt(requestAmountTag);
            sats += Math.floor(millisats / 1000);
            return;
          }
        } catch (error) {
          console.warn('Failed to parse description JSON:', error);
        }
      }

      console.warn('Could not extract amount from zap receipt:', zap.id);
    });


    return { zapCount: count, totalSats: sats, zaps: zapEvents };
  }, [zapEvents]);

  // Check if pending invoice has been paid
  useEffect(() => {
    if (!pendingInvoice || !zapEvents || paymentConfirmed) return;

    // Check if any zap receipt contains our pending invoice
    const isPaid = zapEvents.some(zap => {
      const bolt11Tag = zap.tags.find(([name]) => name === 'bolt11')?.[1];
      return bolt11Tag === pendingInvoice;
    });

    if (isPaid) {
      // If we have multiple invoices pending, show the next one
      if (pendingInvoices.length > 0 && currentInvoiceIndex < pendingInvoices.length - 1) {
        const nextIndex = currentInvoiceIndex + 1;
        setCurrentInvoiceIndex(nextIndex);
        setInvoice(pendingInvoices[nextIndex].invoice);
        setPendingInvoice(pendingInvoices[nextIndex].invoice);

        toast({
          title: `Payment ${currentInvoiceIndex + 1} of ${pendingInvoices.length} received!`,
          description: `Showing invoice ${nextIndex + 1} of ${pendingInvoices.length}...`,
        });

        return; // Don't show confirmation yet, move to next invoice
      }

      // All invoices paid! Show confirmation animation
      setPaymentConfirmed(true);

      // Invalidate all zap-related queries to update counts
      queryClient.invalidateQueries({ queryKey: ['zaps'] });
      queryClient.invalidateQueries({ queryKey: ['track-zaps'] });

      // After showing the confirmation animation, close the dialog
      setTimeout(() => {
        setInvoice(null);
        setPendingInvoice(null);
        setPaymentConfirmed(false);
        setPendingInvoices([]);
        setCurrentInvoiceIndex(0);

        const message = pendingInvoices.length > 1
          ? `All ${pendingInvoices.length} payments confirmed!`
          : 'Your zap has been confirmed.';

        toast({
          title: 'Payment received!',
          description: message,
        });

        // Call success callback
        onZapSuccess?.();
      }, 2000); // Show confirmation for 2 seconds
    }
  }, [pendingInvoice, zapEvents, paymentConfirmed, toast, queryClient, onZapSuccess, pendingInvoices, currentInvoiceIndex]);

  const zap = async (amount: number, comment: string) => {
    if (amount <= 0) {
      return;
    }

    setIsZapping(true);
    setInvoice(null); // Clear any previous invoice at the start
    setPendingInvoice(null); // Clear any previous pending invoice
    setPaymentConfirmed(false); // Clear any previous confirmation state
    setPendingInvoices([]); // Clear any previous pending invoices
    setCurrentInvoiceIndex(0); // Reset invoice index

    if (!user) {
      toast({
        title: 'Login required',
        description: 'You must be logged in to send a zap.',
        variant: 'destructive',
      });
      setIsZapping(false);
      return;
    }

    if (!actualTarget && !targetPubkey) {
      toast({
        title: 'Recipient not found',
        description: 'Could not find the recipient to zap.',
        variant: 'destructive',
      });
      setIsZapping(false);
      return;
    }

    try {
      // Check for zap splits in the event tags
      const zapSplitTags = actualTarget?.tags.filter(([name]) => name === 'zap') || [];

      if (zapSplitTags.length > 0) {
        // Handle zap splits (including single zap tag with custom address)
        await handleZapSplits(zapSplitTags, amount, comment);
      } else {
        // No zap tags - send to author's profile address
        await handleSingleZap(targetPubkey!, amount, comment);
      }
    } catch (err) {
      console.error('Zap error:', err);
      toast({
        title: 'Zap failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      setIsZapping(false);
    }
  };

  const handleZapSplits = async (zapSplitTags: string[][], totalAmount: number, comment: string) => {
    try {
      // Parse zap split tags - format: ["zap", "pubkey", "relay-hint", "weight"]
      // Note: The second field is a pubkey (hex), not a lightning address
      // IMPORTANT: Order is preserved - first tag = first invoice (typically artist), second tag = second invoice (typically uploader)
      const splits = zapSplitTags.map(([, pubkey, , weight]) => ({
        pubkey,
        weight: parseInt(weight || '1'),
      }));

      // Calculate total weight (these are relative weights, not percentages)
      const totalWeight = splits.reduce((sum, split) => sum + split.weight, 0);
      if (totalWeight === 0) {
        throw new Error('Total zap split weight cannot be zero');
      }

      // Calculate split amounts (in sats) using relative weights
      const splitAmounts = splits.map((split) => ({
        pubkey: split.pubkey,
        amount: Math.floor((totalAmount * split.weight) / totalWeight),
      }));

      toast({
        title: splits.length === 1 ? 'Processing zap' : 'Processing split zaps',
        description: splits.length === 1
          ? `Sending ${totalAmount} sats...`
          : `Splitting ${totalAmount} sats between ${splits.length} recipients...`,
      });

      // Create invoices for each split in order (artist first, uploader second, etc.)
      const invoices: Array<{ pubkey: string; amount: number; invoice: string }> = [];

      for (const split of splitAmounts) {
        if (split.amount <= 0) continue; // Skip zero-amount splits

        // Fetch the profile for this pubkey to get their lightning address and zap endpoint
        const invoice = await createZapInvoiceForPubkey(split.pubkey, split.amount, comment);
        if (invoice) {
          invoices.push({ pubkey: split.pubkey, amount: split.amount, invoice });
        }
      }

      if (invoices.length === 0) {
        throw new Error('Failed to create any zap invoices for splits');
      }

      // Get the current active NWC connection dynamically
      const currentNWCConnection = getActiveConnection();

      // Try NWC first if available - pay ALL invoices automatically
      if (currentNWCConnection && currentNWCConnection.connectionString && currentNWCConnection.isConnected) {
        try {
          // Pay all invoices via NWC in sequence
          for (const { invoice, pubkey, amount } of invoices) {
            await sendPayment(currentNWCConnection, invoice);
            console.log(`Paid ${amount} sats to ${pubkey.slice(0, 8)}... via NWC`);
          }

          // Clear states immediately on success
          setIsZapping(false);
          setInvoice(null);
          setPendingInvoice(null);

          toast({
            title: 'Split zaps successful!',
            description: `You sent ${totalAmount} sats split between ${invoices.length} recipient${invoices.length > 1 ? 's' : ''} via NWC.`,
          });

          // Invalidate all zap-related queries to refresh counts
          queryClient.invalidateQueries({ queryKey: ['zaps'] });
          queryClient.invalidateQueries({ queryKey: ['track-zaps'] });

          // Close dialog last to ensure clean state
          onZapSuccess?.();
          return;
        } catch (nwcError) {
          console.error('NWC payment failed for splits, falling back:', nwcError);
          const errorMessage = nwcError instanceof Error ? nwcError.message : 'Unknown NWC error';
          toast({
            title: 'NWC payment failed',
            description: `${errorMessage}. Trying WebLN...`,
            variant: 'destructive',
          });
        }
      }

      // Try WebLN for split payments - pay ALL invoices automatically
      if (webln) {
        try {
          let webLnProvider = webln;
          if (webln.enable && typeof webln.enable === 'function') {
            const enabledProvider = await webln.enable();
            const provider = enabledProvider as WebLNProvider | undefined;
            if (provider) {
              webLnProvider = provider;
            }
          }

          // Pay all invoices via WebLN in sequence
          for (const { invoice, pubkey, amount } of invoices) {
            await webLnProvider.sendPayment(invoice);
            console.log(`Paid ${amount} sats to ${pubkey.slice(0, 8)}... via WebLN`);
          }

          // Clear states immediately on success
          setIsZapping(false);
          setInvoice(null);
          setPendingInvoice(null);

          toast({
            title: 'Split zaps successful!',
            description: `You sent ${totalAmount} sats split between ${invoices.length} recipient${invoices.length > 1 ? 's' : ''}.`,
          });

          // Invalidate all zap-related queries to refresh counts
          queryClient.invalidateQueries({ queryKey: ['zaps'] });
          queryClient.invalidateQueries({ queryKey: ['track-zaps'] });

          // Close dialog last to ensure clean state
          onZapSuccess?.();
          return;
        } catch (weblnError) {
          console.error('WebLN payment failed for splits, falling back:', weblnError);
          const errorMessage = weblnError instanceof Error ? weblnError.message : 'Unknown WebLN error';
          toast({
            title: 'WebLN payment failed',
            description: `${errorMessage}. Please pay manually.`,
            variant: 'destructive',
          });
        }
      }

      // Fallback - Manual payment required
      // Store all invoices and show them one at a time
      setPendingInvoices(invoices);
      setCurrentInvoiceIndex(0);

      // Show message explaining user needs to pay multiple invoices
      toast({
        title: 'Manual payment required',
        description: invoices.length === 1
          ? 'Please scan the QR code to pay the invoice.'
          : `Split zaps require ${invoices.length} separate payments. Showing payment 1 of ${invoices.length}...`,
        variant: 'default',
      });

      // Show the first invoice
      setInvoice(invoices[0].invoice);
      setPendingInvoice(invoices[0].invoice);
      setIsZapping(false);
    } catch (err) {
      console.error('Zap split error:', err);
      toast({
        title: 'Zap split failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      setIsZapping(false);
    }
  };

  const createZapInvoiceForPubkey = async (recipientPubkey: string, amount: number, comment: string): Promise<string | null> => {
    try {
      // Fetch the profile (kind 0) for this pubkey to get their lightning address
      const signal = AbortSignal.timeout(5000);
      const [profileEvent] = await nostr.query([{
        kinds: [0],
        authors: [recipientPubkey],
        limit: 1,
      }], { signal });

      if (!profileEvent) {
        throw new Error(`Profile not found for pubkey: ${recipientPubkey.slice(0, 8)}...`);
      }

      // Parse the profile metadata
      let metadata;
      try {
        metadata = JSON.parse(profileEvent.content);
      } catch (err) {
        throw new Error(`Failed to parse profile metadata for ${recipientPubkey.slice(0, 8)}...`);
      }

      const lightningAddress = metadata.lud16 || metadata.lud06;
      if (!lightningAddress) {
        throw new Error(`No lightning address found for ${recipientPubkey.slice(0, 8)}...`);
      }

      // Get zap endpoint from the profile event
      const zapEndpoint = await nip57.getZapEndpoint(profileEvent as Event);
      if (!zapEndpoint) {
        throw new Error(`No zap endpoint found for ${recipientPubkey.slice(0, 8)}...`);
      }

      // Create zap request for this specific recipient
      const zapAmount = amount * 1000; // convert to millisats

      const zapRequestParams: {
        profile: string;
        event?: Event | string;
        amount: number;
        relays: string[];
      } = {
        profile: recipientPubkey,
        amount: zapAmount,
        relays: [config.relayUrl],
      };

      // Only include event parameter if zapping an event (not a profile)
      if (actualTarget) {
        zapRequestParams.event = (actualTarget.kind >= 30000 && actualTarget.kind < 40000)
          ? actualTarget
          : actualTarget.id;
      }

      let zapRequest = nip57.makeZapRequest(zapRequestParams);

      // Add comment to the content field (NIP-57) - must be done before signing
      // Create a new object to ensure the content is included
      zapRequest = {
        ...zapRequest,
        content: comment,
      };

      // Sign the zap request
      if (!user.signer) {
        throw new Error('No signer available');
      }
      const signedZapRequest = await user.signer.signEvent(zapRequest);

      // Debug: Log the zap request to verify comment is included
      console.log('Zap request (split):', {
        kind: signedZapRequest.kind,
        content: signedZapRequest.content,
        tags: signedZapRequest.tags,
        recipient: recipientPubkey.slice(0, 8)
      });

      // Request invoice from zap endpoint
      const res = await fetch(`${zapEndpoint}?amount=${zapAmount}&nostr=${encodeURI(JSON.stringify(signedZapRequest))}`);
      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${responseData.reason || 'Unknown error'}`);
      }

      const invoice = responseData.pr;
      if (!invoice || typeof invoice !== 'string') {
        throw new Error('Lightning service did not return a valid invoice');
      }

      return invoice;
    } catch (err) {
      console.error(`Failed to create zap invoice for ${recipientPubkey.slice(0, 8)}...:`, err);
      toast({
        title: 'Split zap warning',
        description: `Failed to create invoice for ${recipientPubkey.slice(0, 8)}.... Skipping...`,
        variant: 'destructive',
      });
      return null;
    }
  };

  const handleSingleZap = async (recipientPubkey: string, amount: number, comment: string) => {
    if (!author.data || !author.data?.metadata || !author.data?.event ) {
      toast({
        title: 'Author not found',
        description: 'Could not find the author of this item.',
        variant: 'destructive',
      });
      setIsZapping(false);
      return;
    }

    const { lud06, lud16 } = author.data.metadata;
    if (!lud06 && !lud16) {
      toast({
        title: 'Lightning address not found',
        description: 'The author does not have a lightning address configured.',
        variant: 'destructive',
      });
      setIsZapping(false);
      return;
    }

    // Get zap endpoint using the old reliable method
    const zapEndpoint = await nip57.getZapEndpoint(author.data.event);
    if (!zapEndpoint) {
      toast({
        title: 'Zap endpoint not found',
        description: 'Could not find a zap endpoint for the author.',
        variant: 'destructive',
      });
      setIsZapping(false);
      return;
    }

    // Create zap request - use appropriate event format based on kind
    // For profile zaps (no event), don't include event parameter
    // For addressable events (30000-39999), pass the object to get 'a' tag
    // For all other events, pass the ID string to get 'e' tag
    const zapAmount = amount * 1000; // convert to millisats

    const zapRequestParams: {
      profile: string;
      event?: Event | string;
      amount: number;
      relays: string[];
    } = {
      profile: recipientPubkey,
      amount: zapAmount,
      relays: [config.relayUrl],
    };

    // Only include event parameter if zapping an event (not a profile)
    if (actualTarget) {
      zapRequestParams.event = (actualTarget.kind >= 30000 && actualTarget.kind < 40000)
        ? actualTarget
        : actualTarget.id;
    }

    let zapRequest = nip57.makeZapRequest(zapRequestParams);

    // Add comment to the content field (NIP-57) - must be done before signing
    // Create a new object to ensure the content is included
    zapRequest = {
      ...zapRequest,
      content: comment,
    };

    // Sign the zap request (but don't publish to relays - only send to LNURL endpoint)
    if (!user.signer) {
      throw new Error('No signer available');
    }
    const signedZapRequest = await user.signer.signEvent(zapRequest);

    // Debug: Log the zap request to verify comment is included
    console.log('Zap request (single):', {
      kind: signedZapRequest.kind,
      content: signedZapRequest.content,
      tags: signedZapRequest.tags
    });

    try {
      const res = await fetch(`${zapEndpoint}?amount=${zapAmount}&nostr=${encodeURI(JSON.stringify(signedZapRequest))}`);
          const responseData = await res.json();

          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${responseData.reason || 'Unknown error'}`);
          }

          const newInvoice = responseData.pr;
          if (!newInvoice || typeof newInvoice !== 'string') {
            throw new Error('Lightning service did not return a valid invoice');
          }

          // Get the current active NWC connection dynamically
          const currentNWCConnection = getActiveConnection();

          // Try NWC first if available and properly connected
          if (currentNWCConnection && currentNWCConnection.connectionString && currentNWCConnection.isConnected) {
            try {
              await sendPayment(currentNWCConnection, newInvoice);

              // Clear states immediately on success
              setIsZapping(false);
              setInvoice(null);
              setPendingInvoice(null);

              toast({
                title: 'Zap successful!',
                description: `You sent ${amount} sats via NWC to the author.`,
              });

              // Invalidate all zap-related queries to refresh counts
              queryClient.invalidateQueries({ queryKey: ['zaps'] });
              queryClient.invalidateQueries({ queryKey: ['track-zaps'] });

              // Close dialog last to ensure clean state
              onZapSuccess?.();
              return;
            } catch (nwcError) {
              console.error('NWC payment failed, falling back:', nwcError);

              // Show specific NWC error to user for debugging
              const errorMessage = nwcError instanceof Error ? nwcError.message : 'Unknown NWC error';
              toast({
                title: 'NWC payment failed',
                description: `${errorMessage}. Falling back to other payment methods...`,
                variant: 'destructive',
              });
            }
          }

          if (webln) {  // Try WebLN next
            try {
              // For native WebLN, we may need to enable it first
              let webLnProvider = webln;
              if (webln.enable && typeof webln.enable === 'function') {
                const enabledProvider = await webln.enable();
                // Some implementations return the provider, others return void
                // Cast to WebLNProvider to handle both cases
                const provider = enabledProvider as WebLNProvider | undefined;
                if (provider) {
                  webLnProvider = provider;
                }
              }

              await webLnProvider.sendPayment(newInvoice);

              // Clear states immediately on success
              setIsZapping(false);
              setInvoice(null);
              setPendingInvoice(null);

              toast({
                title: 'Zap successful!',
                description: `You sent ${amount} sats to the author.`,
              });

              // Invalidate all zap-related queries to refresh counts
              queryClient.invalidateQueries({ queryKey: ['zaps'] });
              queryClient.invalidateQueries({ queryKey: ['track-zaps'] });

              // Close dialog last to ensure clean state
              onZapSuccess?.();
            } catch (weblnError) {
              console.error('WebLN payment failed, falling back:', weblnError);

              // Show specific WebLN error to user for debugging
              const errorMessage = weblnError instanceof Error ? weblnError.message : 'Unknown WebLN error';
              toast({
                title: 'WebLN payment failed',
                description: `${errorMessage}. Falling back to other payment methods...`,
                variant: 'destructive',
              });

              setInvoice(newInvoice);
              setPendingInvoice(newInvoice); // Track this invoice for payment detection
              setIsZapping(false);
            }
          } else { // Default - show QR code and manual Lightning URI
            setInvoice(newInvoice);
            setPendingInvoice(newInvoice); // Track this invoice for payment detection
            setIsZapping(false);
          }
        } catch (err) {
          console.error('Zap error:', err);
          toast({
            title: 'Zap failed',
            description: (err as Error).message,
            variant: 'destructive',
          });
          setIsZapping(false);
        }
  };

  const resetInvoice = useCallback(() => {
    setInvoice(null);
    setPendingInvoice(null);
    setPaymentConfirmed(false);
    setPendingInvoices([]);
    setCurrentInvoiceIndex(0);
  }, []);

  return {
    zaps,
    zapCount,
    totalSats,
    ...query,
    zap,
    isZapping,
    invoice,
    setInvoice,
    resetInvoice,
    paymentConfirmed,
    // Multi-invoice state for UI
    pendingInvoices,
    currentInvoiceIndex,
  };
}
